import { useState, useRef, useCallback } from 'react';
import { FormRenderer } from '~/components/FormRenderer';
import { ThemeWrapper } from '~/components/ThemeWrapper';
import { ThankYou } from '~/components/ThankYou';
import { LoadingSpinner } from '~/components/LoadingSpinner';
import { ErrorMessage } from '~/components/ErrorMessage';
import { useFormSchema } from '~/hooks/useFormSchema';
import { useFormSubmission } from '~/hooks/useFormSubmission';
import { useUTMCapture } from '~/hooks/useUTMCapture';
import { usePreviewMode } from '~/hooks/usePreviewMode';
import { trackEvent } from '~/services/posthog';

const noop = () => {};

export const App = () => {
  const { isPreviewMode, schema: previewSchema, loading: previewLoading } = usePreviewMode();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const schemaUpdateCounter = useRef(0);

  // In preview mode, skip Supabase fetch, submissions, and UTM capture
  const {
    schema: fetchedSchema,
    formId,
    formVersion,
    loading: schemaLoading,
    error: schemaError,
  } = useFormSchema({ enabled: !isPreviewMode });
  const utmParams = useUTMCapture();

  const {
    saveSubmission,
    markComplete,
    error: submissionError,
  } = useFormSubmission({
    formId: formId || '',
    formVersion: formVersion || 1,
    utmParams,
  });

  // Pick the right schema source
  const schema = isPreviewMode ? previewSchema : fetchedSchema;
  const loading = isPreviewMode ? previewLoading : schemaLoading;

  // Track schema updates for preview re-renders
  if (isPreviewMode && previewSchema) {
    schemaUpdateCounter.current += 1;
  }

  const handleFormChange = useCallback(
    async (formData: Record<string, unknown>, currentStep?: number) => {
      if (formId && formVersion) {
        await saveSubmission(formData, currentStep);
      }
    },
    [formId, formVersion, saveSubmission],
  );

  const handleFormSubmit = useCallback(
    async (formData: Record<string, unknown>) => {
      if (!formId || !formVersion) return;

      try {
        await saveSubmission(formData);
        await markComplete();
        trackEvent('form_submitted', { form_id: formId, email: formData.email });
        setIsSubmitted(true);
      } catch (error) {
        console.error('Error submitting form:', error);
      }
    },
    [formId, formVersion, saveSubmission, markComplete],
  );

  if (loading) {
    if (isPreviewMode) {
      return (
        <div className="flex items-center justify-center min-h-[200px] text-muted-foreground text-sm">
          Waiting for schema from editor...
        </div>
      );
    }
    return <LoadingSpinner />;
  }

  if (!isPreviewMode && schemaError) {
    return <ErrorMessage message={schemaError} />;
  }

  if (!schema) {
    if (isPreviewMode) {
      return (
        <div className="flex items-center justify-center min-h-[200px] text-muted-foreground text-sm">
          Waiting for schema from editor...
        </div>
      );
    }
    return <ErrorMessage message="Form not found" />;
  }

  // Show ThankYou only for forms without a built-in completion step.
  // Multi-step forms with a completion step (last step has no fields) handle this inline.
  const hasCompletionStep = schema.steps?.length ? schema.steps[schema.steps.length - 1].fields.length === 0 : false;

  if (!isPreviewMode && isSubmitted && !hasCompletionStep) {
    const action = schema.completionAction;
    const shouldRedirect = action?.type === 'redirect' || action?.type === 'both';
    const shouldShowMessage = action?.type === 'message' || action?.type === 'both';

    if (shouldRedirect && action?.url) {
      return <ThankYou message={action.message} redirectUrl={action.url} />;
    }

    if (shouldShowMessage) {
      return <ThankYou message={action?.message} />;
    }

    return <ThankYou />;
  }

  return (
    <ThemeWrapper theme={schema.theme}>
      <div className={isPreviewMode ? '' : 'min-h-screen bg-background py-8'}>
        <FormRenderer
          key={isPreviewMode ? schemaUpdateCounter.current : undefined}
          schema={schema}
          onSubmit={isPreviewMode ? noop : handleFormSubmit}
          onChange={isPreviewMode ? undefined : handleFormChange}
        />
        {!isPreviewMode && submissionError && (
          <div className="max-w-2xl mx-auto mt-4 px-6">
            <div className="bg-destructive/10 border border-destructive/20 rounded p-4 text-destructive text-sm">
              {submissionError}
            </div>
          </div>
        )}
      </div>
    </ThemeWrapper>
  );
};
